import { auth, database }  from './initFirebase';
import { usesDockmaster } from './marinaConfig';
import { DB_URL, FUNCTIONS_BASE } from './firebaseConfig';
import { ref as authRef, onAuthStateChanged } from "firebase/auth";
import { ref , get as firebaseGet, child, onValue, runTransaction, push as dbPush, update as dbUpdate } from "firebase/database";
import { getFunctions, httpsCallable } from 'firebase/functions';

// Standalone duplicate folder — single Firebase project, no prod/test branching.
const currentURL = DB_URL;

const VALID_ROLES = new Set(['admin', 'tech', 'clerk', 'office', 'dock']);

// role is a free-form comma list ("tech, yard", "admin, tech", "admin, office")
// — confirmed against live /users records — not a single fixed value, so
// membership needs a token check. An exact-string check here previously
// locked every compound-role account (any admin+tech or admin+office combo)
// out of isAdminRole's admin-only gates, and out of the app entirely via
// isValidRole below (its "no role assigned" branch fires for the whole
// string not matching any single VALID_ROLES entry).
function roleTokens(role) {
  return (role || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
}

export function isAdminRole(role) {
  return roleTokens(role).includes('admin');
}

export function isValidRole(role) {
  return roleTokens(role).some(t => VALID_ROLES.has(t));
}

document.addEventListener("DOMContentLoaded", getCurrentDatabaseVersion);


export { FUNCTIONS_BASE };

// Single-environment duplicate — always 'test' here (there is no prod in this folder).
export const ENV = 'test';



let indexedVersion;

// Object stores added after initial ship. getDB() below always reuses whatever
// version already exists in a returning user's browser (no upgrade = no new
// stores), so any store added here has to be created via this one-time bump
// the first time a returning user's app loads — new installs get it for free
// via the normal ensure()-based upgrade path in getDB().
const STORES_ADDED_POST_LAUNCH = [
  { name: 'estimateData', opts: { keyPath: 'id' } },
  { name: 'pesticideData', opts: { keyPath: 'id' } },
  { name: 'inventoryPOs', opts: { keyPath: 'id' } },
  { name: 'batteryUnits', opts: { keyPath: 'id' } },
  { name: 'batteryReadings', opts: { keyPath: 'id' } },
  { name: 'invoiceData', opts: { keyPath: 'id' } },
  { name: 'paymentData', opts: { keyPath: 'id' } },
];

// The full store list, baseline + post-launch. getDB()'s own onupgradeneeded
// (below) never fires once this module's version-check has already bumped
// the database to its target version, so on a *truly* brand-new database —
// zero stores, nothing to be "missing" from — the version-check's own
// upgrade transaction has to create everything, not just the
// STORES_ADDED_POST_LAUNCH subset, or baseline stores like inventoryData
// would silently never get created.
const BASELINE_STORES = [
  { name: 'customerData', opts: { keyPath: 'id' } },
  { name: 'jobData', opts: { keyPath: 'id' } },
  { name: 'closedJobs', opts: { keyPath: 'id' } },
  { name: 'opCodeData', opts: { keyPath: 'id' } },
  { name: 'mapDataBoats', opts: { keyPath: 'id' } },
  { name: 'mapDataMoorings', opts: { keyPath: 'id' } },
  { name: 'inventoryData', opts: { keyPath: 'id' } },
  { name: 'receivingData', opts: { keyPath: 'id' } },
  { name: 'mooringDatabase', opts: { keyPath: 'id' } },
  { name: 'rentalDatabase', opts: { keyPath: 'id' } },
  { name: 'vendorData', opts: { keyPath: 'id' } },
  { name: 'scheduleData', opts: { keyPath: 'id' } },
  { name: 'techData', opts: { keyPath: 'id' } },
  { name: 'notificationData', opts: { keyPath: 'id' } },
  { name: 'metadata', opts: { keyPath: 'key' } },
];

let __versionCheckPromise = null;

// Memoized so every caller — the DOMContentLoaded kickoff below and getDB()'s
// await of it — shares the same in-flight check instead of racing separate
// indexedDB.open() calls against each other.
export function getCurrentDatabaseVersion() {
  if (__versionCheckPromise) return __versionCheckPromise;
  const dbName = 'openDatabase';
  __versionCheckPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName);

    request.onsuccess = (event) => {
      const db = event.target.result;
      const missingBaseline = BASELINE_STORES.filter(s => !db.objectStoreNames.contains(s.name));
      const missingPostLaunch = STORES_ADDED_POST_LAUNCH.filter(s => !db.objectStoreNames.contains(s.name));
      const missingStores = [...missingBaseline, ...missingPostLaunch];

      if (missingStores.length === 0) {
        const version = db.version;
        indexedVersion = version;
        db.close();
        resolve(version);
        return;
      }

      const targetVersion = db.version + 1;
      db.close();

      const upgradeRequest = indexedDB.open(dbName, targetVersion);
      upgradeRequest.onupgradeneeded = (e) => {
        const upgradeDb = e.target.result;
        missingStores.forEach(s => {
          if (!upgradeDb.objectStoreNames.contains(s.name)) {
            upgradeDb.createObjectStore(s.name, s.opts);
          }
        });
      };
      upgradeRequest.onsuccess = (e) => {
        const upgradedDb = e.target.result;
        indexedVersion = upgradedDb.version;
        upgradedDb.close();
        resolve(indexedVersion);
      };
      upgradeRequest.onerror = (e) => {
        console.error('Error upgrading IndexedDB for new stores:', e.target.error);
        indexedVersion = db.version;
        resolve(indexedVersion);
      };
      upgradeRequest.onblocked = () => {
        console.warn('IndexedDB upgrade blocked by another open tab — new stores will be created next load.');
        indexedVersion = db.version;
        resolve(indexedVersion);
      };
    };

    request.onerror = (event) => {
      console.error('Error checking database version:', event.target.errorCode);
      resolve(null); // In case of error, resolve with null
    };
  });
  return __versionCheckPromise;
}

let __dbPromise = null;

export function getDB(version) {
  if (__dbPromise) return __dbPromise;

  // Await the (memoized) one-time version-bump check first. Without this,
  // whichever of the app's many DOMContentLoaded handlers calls getDB()
  // first could win a race against getCurrentDatabaseVersion() finishing,
  // open the DB at its un-migrated version, and cache that connection in
  // __dbPromise forever — silently missing any store in
  // STORES_ADDED_POST_LAUNCH for the rest of the session.
  __dbPromise = getCurrentDatabaseVersion().then(() => new Promise((resolve, reject) => {
    const versionToUse =
      (typeof indexedVersion !== 'undefined' && indexedVersion)
        ? indexedVersion
        : version;

    const req = versionToUse
      ? indexedDB.open('openDatabase', versionToUse)
      : indexedDB.open('openDatabase');

    req.onupgradeneeded = (e) => {
      const db = e.target.result;

      const ensure = (name, opts) => {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, opts);
      };

      ensure('customerData', { keyPath: 'id' });
      ensure('jobData', { keyPath: 'id' });
      ensure('closedJobs', { keyPath: 'id' });
      ensure('opCodeData', { keyPath: 'id' });
      ensure('mapDataBoats', { keyPath: 'id' });
      ensure('mapDataMoorings', { keyPath: 'id' });
      ensure('inventoryData', { keyPath: 'id' });
      ensure('receivingData', { keyPath: 'id' });
      ensure('mooringDatabase', { keyPath: 'id' });
      ensure('rentalDatabase', { keyPath: 'id' });
      ensure('vendorData', { keyPath: 'id' });
      ensure('scheduleData', { keyPath: 'id' });
      ensure('techData', { keyPath: 'id' });
      ensure('notificationData', { keyPath: 'id' });
      ensure('estimateData', { keyPath: 'id' });
      ensure('pesticideData', { keyPath: 'id' });
      ensure('inventoryPOs', { keyPath: 'id' });
      ensure('batteryUnits', { keyPath: 'id' });
      ensure('batteryReadings', { keyPath: 'id' });
      ensure('invoiceData', { keyPath: 'id' });
      ensure('paymentData', { keyPath: 'id' });
      ensure('metadata', { keyPath: 'key' });

      const tx = e.target.transaction;
      const metadataStore = tx.objectStore('metadata');

      const ensureMetadataKey = (key) => {
        const getReq = metadataStore.get(key);
        getReq.onsuccess = () => {
          if (!getReq.result) metadataStore.put({ key, timestamp: 0 });
        };
      };

      ensureMetadataKey('customerDataLastUpdated');
      ensureMetadataKey('jobDataLastUpdated');
      ensureMetadataKey('closedJobsLastUpdated');
      ensureMetadataKey('opCodeDataLastUpdated');
      ensureMetadataKey('boatDataLastUpdated');
      ensureMetadataKey('mooringDataLastUpdated');
      ensureMetadataKey('inventoryDataLastUpdated');
      ensureMetadataKey('receivingDataLastUpdated');
      ensureMetadataKey('mooringDatabaseLastUpdated');
      ensureMetadataKey('rentalDatabaseLastUpdated');
      ensureMetadataKey('vendorDataLastUpdated');
      ensureMetadataKey('scheduleDataLastUpdated');
      ensureMetadataKey('techDataLastUpdated');
      ensureMetadataKey('settingsLastUpdated');
      ensureMetadataKey('inventoryPOsLastUpdated');
    };

    req.onerror = () => {
      // Don't let a failed open (e.g. a VersionError from racing another
      // same-origin frame's version bump — this app runs many iframes,
      // each with their own copy of this module and their own long-lived
      // getDB() connection) permanently poison this frame for the rest of
      // the session. Reset so the next caller gets a fresh attempt instead
      // of the same cached rejected promise forever.
      __dbPromise = null;
      reject(req.error);
    };
    req.onsuccess = () => {
      const db = req.result;
      // Standard IndexedDB multi-tab pattern: if another same-origin frame
      // needs to bump the DB version (e.g. STORES_ADDED_POST_LAUNCH gained
      // an entry), it can't proceed while this connection sits open at the
      // old version. Auto-close on versionchange so that bump isn't
      // blocked — without this, other frames fall back to opening at the
      // stale version once this one wins the bump, which throws.
      db.onversionchange = () => db.close();
      resolve(db);
    };
  }));

  return __dbPromise;
}


export async function idbGet(storeName, key, version) {
  const db = await getDB(version);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

export async function idbPut(storeName, value, version) {
  if (blockIfReadOnly('Saving')) return false;
  const db = await getDB(version);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).put(value);
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
}

export async function idbDelete(storeName, key, version) {
  if (blockIfReadOnly('Deleting')) return false;
  const db = await getDB(version);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).delete(key);
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
}

// The shell's RTDB listeners (app.js) keep jobData/customerData in IndexedDB current
// for every user's changes. Page iframes read that cache once at load, so the shell
// announces each update here and a page can rebuild from the cache (onLocalDataChanged).
const DATA_CHANNEL = 'marina-local-data';
let dataChannel;
function getDataChannel() {
  if (!dataChannel && typeof BroadcastChannel !== 'undefined') dataChannel = new BroadcastChannel(DATA_CHANNEL);
  return dataChannel;
}
export function notifyLocalDataChanged(store) {
  try { getDataChannel()?.postMessage({ store }); } catch { /* best effort */ }
}
/** Calls handler(store) after jobData/customerData in IndexedDB changed; batches bursts. */
export function onLocalDataChanged(handler, delayMs = 400) {
  const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(DATA_CHANNEL) : null;
  if (!channel) return () => {};
  let timer;
  const stores = new Set();
  channel.onmessage = (e) => {
    stores.add(e.data?.store);
    clearTimeout(timer);
    timer = setTimeout(() => { const s = [...stores]; stores.clear(); handler(s); }, delayMs);
  };
  return () => { clearTimeout(timer); channel.close(); };
}

export async function idbCursorAll(storeName, version, filterFn) {
  const db = await getDB(version);
  return new Promise((resolve, reject) => {
    const out = [];
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.openCursor();

    req.onsuccess = (e) => {
      const c = e.target.result;
      if (!c) return resolve(out);
      if (c.key !== 'lastUpdated') {
        const v = c.value;
        if (!filterFn || filterFn(v, c.key)) out.push(v);
      }
      c.continue();
    };

    req.onerror = () => reject(req.error);
  });
}

export function generateId() {
  return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  );
}

  /**
   * A page's own toast (an element with [hidden] and an .error look): pageToast('rq-toast') →
   * toast(message, isError). Errors stay 6 s, the rest 3 s.
   */
  export function pageToast(id) {
    let timer;
    return (msg, error) => {
      const t = document.getElementById(id);
      if (!t) return;
      t.textContent = msg;
      t.classList.toggle('error', !!error);
      t.hidden = false;
      clearTimeout(timer);
      timer = setTimeout(() => { t.hidden = true; }, error ? 6000 : 3000);
    };
  }

  // Toasts look like the "New version ready" prompt (updatePrompt.css): a navy
  // pill with a round icon. The old solid green/red box is gone; the status
  // colour callers pass ('green', 'red', 'blue', 'yellow', a hex) now only picks
  // the icon. The styles are injected here because #snackbar lives on many
  // pages, each with its own legacy #snackbar rule.
  const TOAST_ICONS = {
    ok: '<path d="M20 6 9 17l-5-5"/>',
    error: '<circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5"/><path d="M12 16.5h.01"/>',
    warn: '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
    info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 7.5h.01"/>',
  };

  function toastTone(status) {
    const s = String(status || '').toLowerCase();
    if (s === 'green') return 'ok';
    if (s === 'red') return 'error';
    if (s === 'yellow' || s === 'orange') return 'warn';
    return 'info';
  }

  function ensureToastStyles() {
    if (document.getElementById('app-toast-styles')) return;
    const css = document.createElement('style');
    css.id = 'app-toast-styles';
    css.textContent = `
#snackbar.app-toast.app-toast {
  visibility: hidden; position: fixed; left: 50%; right: auto; top: auto; bottom: 24px; z-index: 100000;
  display: flex; align-items: center; gap: 12px; width: max-content; min-width: 0; max-width: calc(100vw - 32px);
  margin: 0; padding: 8px 18px 8px 8px; border: 0; outline: 0; border-radius: 999px;
  background: var(--harbor-navy, #0C2C49); color: #fff; box-shadow: 0 12px 30px rgba(10,33,53,.28);
  font: 600 13.5px/1.3 var(--font-body, system-ui, sans-serif); text-align: left;
  opacity: 0; transform: translate(-50%, 16px); transition: opacity .2s ease, transform .2s ease, visibility 0s linear .2s;
  animation: none; pointer-events: none;
}
#snackbar.app-toast.app-toast.show { visibility: visible; opacity: 1; transform: translate(-50%, 0); transition: opacity .2s ease, transform .2s ease; }
#snackbar.app-toast .app-toast-icon {
  flex: none; display: grid; place-items: center; width: 32px; height: 32px; border-radius: 50%;
  background: rgba(95,208,196,.18); color: var(--mist, #5FD0C4);
}
#snackbar.app-toast[data-tone="error"] .app-toast-icon { background: rgba(255,107,107,.18); color: #FF8A8A; }
#snackbar.app-toast[data-tone="warn"] .app-toast-icon { background: rgba(245,183,64,.2); color: #F5C451; }
#snackbar.app-toast[data-tone="info"] .app-toast-icon { background: rgba(120,170,255,.18); color: #9CC2FF; }
#snackbar.app-toast .app-toast-text { min-width: 0; overflow-wrap: anywhere; }
@media (max-width: 600px) { body.has-bottom-bar #snackbar.app-toast.app-toast { bottom: calc(var(--mbar-h, 64px) + 12px); } }
body:has(#update-prompt) #snackbar.app-toast.app-toast { bottom: 88px; }
@media (max-width: 600px) { body.has-bottom-bar:has(#update-prompt) #snackbar.app-toast.app-toast { bottom: calc(var(--mbar-h, 64px) + 76px); } }
@media print { #snackbar.app-toast { display: none !important; } }`;
    document.head.appendChild(css);
  }

  let toastTimer;
  export function toastMessage(message, status, time) {
    var x = document.getElementById("snackbar");
    if (!x) return;
    ensureToastStyles();

    const tone = toastTone(status);
    x.style.backgroundColor = '';
    x.style.animation = '';
    x.classList.add('app-toast');
    x.dataset.tone = tone;
    x.setAttribute('role', tone === 'error' ? 'alert' : 'status');
    x.innerHTML = `<span class="app-toast-icon"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${TOAST_ICONS[tone]}</svg></span><span class="app-toast-text"></span>`;
    x.querySelector('.app-toast-text').textContent = message;

    // Restart the slide-in when a toast replaces one still showing.
    x.classList.remove('show');
    void x.offsetWidth;
    x.classList.add('show');

    // A newer toast resets the clock, so an older timer can't hide it early.
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => x.classList.remove('show'), time || 3000);
  }

  // ─── Offline read-only mode ─────────────────────────────────────────────
  // The app shell (app.js) decides when it's offline and broadcasts
  // { type: 'APP_MODE', readOnly } to every page iframe. Each page bundle has
  // its own copy of this module, so an iframe only knows it's read-only
  // through that message — the listener below is what makes blockIfReadOnly()
  // work inside pages at all. Browsing stays usable; writes are what's blocked.
  let __READ_ONLY_MODE__ = false;

const IN_SHELL_IFRAME = (() => {
  try { return window.parent !== window && window.parent.location.origin === window.location.origin; }
  catch { return false; }
})();

export function setReadOnlyMode(v) {
  __READ_ONLY_MODE__ = !!v;
  document.documentElement.toggleAttribute('data-readonly', __READ_ONLY_MODE__);
}

export function isReadOnlyMode() {
  return __READ_ONLY_MODE__;
}

// Pages without their own #snackbar hand the message to the shell to show.
function readOnlyNotice(message) {
  if (!document.getElementById('snackbar') && IN_SHELL_IFRAME) {
    window.parent.postMessage({ type: 'APP_TOAST', message, color: 'red' }, window.location.origin);
    return;
  }
  try { toastMessage(message, 'red'); } catch {}
}

export function blockIfReadOnly(action = 'This action') {
  if (!__READ_ONLY_MODE__) return false;
  readOnlyNotice(`${action} is disabled while offline.`);
  return true;
}

if (IN_SHELL_IFRAME) {
  window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin || event.data?.type !== 'APP_MODE') return;
    setReadOnlyMode(event.data.readOnly);
  });
  // Pages load lazily, often after the shell already broadcast — ask for the current mode.
  window.parent.postMessage({ type: 'APP_MODE_REQUEST' }, window.location.origin);
}

// Direct REST/Cloud Function writes made while offline fail with a bare
// "Failed to fetch" (or worse, half-send on a flaky link). Stop them up front
// with a clear message instead. Reads (GET) pass through untouched.
const WRITE_HOSTS = /(^|\.)(firebaseio\.com|cloudfunctions\.net|run\.app|firebasestorage\.app)$/;
let lastWriteNoticeAt = 0;
if (!window.__readOnlyFetchGuard) {
  window.__readOnlyFetchGuard = true;
  const realFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    if (__READ_ONLY_MODE__) {
      const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
      let host = '';
      try { host = new URL(input instanceof Request ? input.url : String(input), window.location.href).hostname; } catch {}
      if (method !== 'GET' && method !== 'HEAD' && WRITE_HOSTS.test(host)) {
        if (Date.now() - lastWriteNoticeAt > 3000) {
          lastWriteNoticeAt = Date.now();
          readOnlyNotice("You're offline — this change was not saved.");
        }
        return Promise.reject(new Error('Offline read-only mode: write blocked'));
      }
    }
    return realFetch(input, init);
  };
}

  function checkAuthState() {
    return new Promise((resolve, reject) => {
      onAuthStateChanged(auth, (user) => {
        if (user) {
          resolve(user);
        } else {
          reject(new Error('User is not authenticated'));
        }
      });
    });
  }
  

  let cachedIdToken = null;
  let tokenExpirationTime = 0;
  
  export async function fetchIdToken() {
      const user = await checkAuthState();
      if (!user) {
          throw new Error("User not authenticated");
      }
  
      // Check if cached token is still valid
      const currentTime = Date.now();
      if (cachedIdToken && currentTime < tokenExpirationTime) {
          return cachedIdToken;
      }
  
      try {
          const idTokenResult = await user.getIdTokenResult();
          cachedIdToken = idTokenResult.token;
          tokenExpirationTime = idTokenResult.expirationTime ? new Date(idTokenResult.expirationTime).getTime() - 60000 : currentTime + 300000; // Cache for ~5 minutes
  
          return cachedIdToken;
      } catch (error) {
          console.error("Error fetching ID token:", error);
          throw error;
      }
  }
  

  export async function fetchFirebaseLastUpdated(firebasePath) {
    try {
      const idToken = await fetchIdToken();
      const firebaseUrl = `${currentURL}/${firebasePath}/lastUpdated.json?auth=${idToken}`;
  
      // Fetch the lastUpdated value
      const response = await fetch(firebaseUrl);
      if (!response.ok) throw new Error(`Firebase fetch failed with status ${response.status}`);
  
      const timestamp = await response.json();
  
      // If lastUpdated doesn't exist, set it to the current timestamp
      if (timestamp === null) {
        const currentTimestamp = Date.now();
        const updateUrl = `${currentURL}/${firebasePath}/lastUpdated.json?auth=${idToken}`;
        const updateResponse = await fetch(updateUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(currentTimestamp),
        });
  
        if (!updateResponse.ok) {
          throw new Error(`Failed to update lastUpdated with status ${updateResponse.status}`);
        }
  
        console.log(`lastUpdated set to ${currentTimestamp} for ${firebasePath}`);
        return currentTimestamp;
      }
  
      return timestamp;
    } catch (error) {
      console.error(`Error fetching or updating Firebase last updated timestamp for ${firebasePath}:`, error);
      throw error;
    }
  }

  export async function fetchIndexedDBLastUpdated(db, metadataKeyName) {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['metadata'], 'readonly');
      const store = transaction.objectStore('metadata');
      const request = store.get(metadataKeyName);
  
      request.onsuccess = () => {
        resolve(request.result ? request.result.timestamp : 0);
      };
  
      request.onerror = (event) => {
        console.error(`Error fetching IndexedDB last updated timestamp for ${metadataKeyName}:`, event.target.error);
        reject(event.target.error);
      };
    });
  }

  export async function updateTimestamps(dataStoreNameIDB, dataStoreNameFirebase, timestamp) {
    // Timestamp to use for both IndexedDB and Firebase updates
    let currentTimestamp = timestamp ? timestamp : new Date().getTime();
  
    try {
      // Update timestamp in IndexedDB
      const db = await getDB();
      const tx = db.transaction(['metadata'], 'readwrite');
      const store = tx.objectStore('metadata');
      const key = `${dataStoreNameIDB}LastUpdated`;
      store.put({ key: key, timestamp: currentTimestamp });
  
      // Update timestamp in Firebase
      const firebasePath = `${dataStoreNameFirebase}/lastUpdated`;
      if(dataStoreNameFirebase !== 'skip'){
      await updateFirebaseTimestamp(firebasePath, currentTimestamp);
      }
    } catch (error) {
      console.error(`Error updating timestamps for ${dataStoreNameIDB}:`, error);
    }
  }

export async function updateFirebaseTimestamp(path, timestamp) {
  if (blockIfReadOnly('Syncing')) return false;
  const idToken = await fetchIdToken();
  const firebaseUrl = `${currentURL}/${path}.json?auth=${idToken}`;
  const response = await fetch(firebaseUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(timestamp),
  });
  if (!response.ok) throw new Error('Firebase update failed');
}


// ---------- Estimates (shared across app.js's Work Order/Estimate creation
// modal and mooringEstimator.js's Mooring Repair Estimator — both build the
// same estimates/{id} record shape and need the same numbering + save path,
// but live in separate webpack bundles with no other shared-module boundary
// between them besides this file). ----------

export async function nextEstimateNumber() {
  const year = new Date().getFullYear();
  const counterRef = ref(database, `estimateCounters/${year}`);
  const result = await runTransaction(counterRef, (current) => (current || 0) + 1);
  const seq = result.snapshot.val();
  return `EST-${year}-${String(seq).padStart(3, '0')}`;
}

// Writes an estimate record to both Firebase (source of truth) and the
// IndexedDB mirror. Shared by every place that changes an estimate: the
// initial save, status transitions, duplication, and conversion.
export async function saveEstimateRecord(record) {
  if (blockIfReadOnly('Saving an estimate')) return false;
  const idToken = await fetchIdToken();
  const path = `/estimates/${record.id}.json?auth=${idToken}`;
  const response = await fetch(currentURL + path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(record)
  });
  if (!response.ok) throw new Error(`Firebase save failed with status ${response.status}`);
  await idbPut('estimateData', record);
  await updateTimestamps('estimateData', 'estimates');
  return true;
}

// ---------- Inventory purchase orders (Inventory redesign — Reorder/Receiving tabs) ----------

export async function nextPurchaseOrderNumber() {
  const year = new Date().getFullYear();
  const counterRef = ref(database, `poCounters/${year}`);
  const result = await runTransaction(counterRef, (current) => (current || 0) + 1);
  const seq = result.snapshot.val();
  return `PO-${year}-${String(seq).padStart(3, '0')}`;
}

// Writes a purchase-order record to both Firebase (source of truth) and the
// IndexedDB mirror. Shared by draft-PO creation (Reorder tab) and receipt
// posting (Receiving tab), mirroring saveEstimateRecord's shape above.
export async function savePurchaseOrderRecord(record) {
  if (blockIfReadOnly('Saving a purchase order')) return false;
  const idToken = await fetchIdToken();
  const path = `/inventoryPOs/${record.id}.json?auth=${idToken}`;
  const response = await fetch(currentURL + path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(record)
  });
  if (!response.ok) throw new Error(`Firebase save failed with status ${response.status}`);
  await idbPut('inventoryPOs', record);
  await updateTimestamps('inventoryPOs', 'inventoryPOs');
  return true;
}

// ---------- Pesticide tracking ----------
// Shared by app.js's work-order-completion capture hook and inventory.js's
// manual-entry/report views — two separate webpack bundles both writing the
// same pesticideData/{year}/{entryId} record shape and both needing to
// deduct inventory when a tagged material is used.

export async function logPesticideApplication(entry) {
  if (blockIfReadOnly('Logging pesticide application')) return false;
  const year = new Date(entry.Apply_Date || Date.now()).getFullYear();
  const id = entry.id || generateId();
  const record = { ...entry, id, Year: year };
  const idToken = await fetchIdToken();
  const path = `/pesticideData/${year}/${id}.json?auth=${idToken}`;
  const response = await fetch(currentURL + path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(record)
  });
  if (!response.ok) throw new Error(`Firebase save failed with status ${response.status}`);
  await idbPut('pesticideData', record);
  await updateTimestamps('pesticideData', 'pesticideData');
  return record;
}

// Adjusts an inventory item's on-hand quantity by deltaQty (negative to
// deduct) in both Firebase (source of truth) and the IndexedDB mirror.
// Unlike app.js's older reduceInventory() (IndexedDB-only), this writes
// through to Firebase too — pesticide-deduction accuracy is regulatory
// filing data, not just a shop-floor convenience number.
export async function adjustInventoryQuantity(partId, deltaQty) {
  if (blockIfReadOnly('Adjusting inventory')) return false;
  const item = await idbGet('inventoryData', partId);
  if (!item) throw new Error(`Inventory item ${partId} not found`);
  // Inventory redesign (design_handoff_inventory_fuel_dashboard) added
  // per-bin stock (item.Bins). Deduct from the first bin with stock (else
  // bin 0) so Qty stays in sync with sum(Bins) instead of drifting from it.
  const bins = Array.isArray(item.Bins) && item.Bins.length
    ? item.Bins.map(b => ({ ...b }))
    : [{ name: item.Location || 'Unassigned', qty: Number(item.Qty) || 0 }];
  let remaining = -deltaQty;
  if (remaining > 0) {
    const idx = bins.findIndex(b => b.qty > 0) >= 0 ? bins.findIndex(b => b.qty > 0) : 0;
    bins[idx].qty = Math.max(0, bins[idx].qty - remaining);
  } else if (remaining < 0) {
    bins[0].qty = Math.max(0, bins[0].qty - remaining);
  }
  const newQty = bins.reduce((s, b) => s + (Number(b.qty) || 0), 0);
  const updated = { ...item, Bins: bins, Qty: newQty, Location: bins[0].name };
  const idToken = await fetchIdToken();
  const path = `/inventory/${partId}.json?auth=${idToken}`;
  const response = await fetch(currentURL + path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ Bins: bins, Qty: newQty, Location: bins[0].name })
  });
  if (!response.ok) throw new Error(`Firebase update failed with status ${response.status}`);
  await idbPut('inventoryData', updated);
  await updateTimestamps('inventoryData', 'inventory');
  return updated;
}

// Persists the result of an EPA PPLS registration check onto an inventory
// item, so the ☣ badge elsewhere can flag a lapsed/cancelled registration
// without re-querying EPA every time it's shown.
export async function updateInventoryEpaStatus(partId, status, checkedAtIso) {
  if (blockIfReadOnly('Recording EPA registration status')) return false;
  const item = await idbGet('inventoryData', partId);
  if (!item) throw new Error(`Inventory item ${partId} not found`);
  const updated = { ...item, EPA_Status: status, EPA_Status_Checked: checkedAtIso };
  const idToken = await fetchIdToken();
  const path = `/inventory/${partId}.json?auth=${idToken}`;
  const response = await fetch(currentURL + path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ EPA_Status: status, EPA_Status_Checked: checkedAtIso })
  });
  if (!response.ok) throw new Error(`Firebase update failed with status ${response.status}`);
  await idbPut('inventoryData', updated);
  return updated;
}

// DockMaster helpers. A marina whose billing system is the app's own (Settings › Features,
// marinaConfig.js) never calls DockMaster: each helper answers "nothing from DockMaster"
// instead, so every caller keeps working. The endpoints only answer signed-in staff.
const dmHeaders = async (extra = {}) => ({ ...extra, Authorization: `Bearer ${await fetchIdToken()}` });

/** Next invoice / receipt / credit / statement number from the server ("INV-2026-0001"). */
export async function nextDocNumber(kind) {
  const res = await httpsCallable(getFunctions(), 'nextDocNumber')({ kind });
  return res.data.number;
}

/** Next work order number from the app's own sequence ("WO-2241"). */
export async function nextInternalWorkOrderNumber() {
  const result = await runTransaction(ref(database, '/meta/nextWorkOrderSeq'), curr => (curr || 2200) + 1);
  return `WO-${result.snapshot.val()}`;
}

export async function fetchDMWorkOrder(workOrder, billDate, invoiceNumber) {
  if (!usesDockmaster()) return { Operations: [] };
  const u = new URL(`${FUNCTIONS_BASE}/dockmasterRetrieveWorkOrder`);
  u.searchParams.set('workOrder', workOrder);
  u.searchParams.set('detail', 'true');
  u.searchParams.set('env', ENV);
  if (billDate) u.searchParams.set('billDate', billDate);
  if (invoiceNumber) u.searchParams.set('invoiceNumber', invoiceNumber);
  const r = await fetch(u.toString(), { headers: await dmHeaders() });
  if (!r.ok) throw new Error(`DM workorder failed ${r.status}`);
  return r.json();
}


export async function updateDMWorkOrder(payload, overwriteComments = false) {
  if (!usesDockmaster()) return { skipped: 'no DockMaster' };
  const u = new URL(`${FUNCTIONS_BASE}/dockmasterUpdateWorkOrder`);
  u.searchParams.set('overwriteComments', String(overwriteComments));
  const r = await fetch(u.toString(), { method: 'POST', headers: await dmHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(payload) });
  if (!r.ok) throw new Error(`DM update failed ${r.status}`);
  return r.json();
}

export async function deleteDMOperationProxy(WOId, Operation) {
  if (!usesDockmaster()) return { skipped: 'no DockMaster' };
  const u = new URL(`${FUNCTIONS_BASE}/dockmasterDeleteOperation`);
  u.searchParams.set('WOId', WOId);
  u.searchParams.set('Operation', Operation);
  const r = await fetch(u.toString(), { method: 'POST', headers: await dmHeaders() });
  if (!r.ok) throw new Error(`DM delete failed ${r.status}`);
  return r.json();
}

export async function fetchDockmasterCustomer(customerId) {
  if (!usesDockmaster()) return { Invoices: [] };
  const u = new URL(`${FUNCTIONS_BASE}/dockmasterRetrieveCustomer`);
  u.searchParams.set('customerId', customerId);
  u.searchParams.set('env', ENV);
  const r = await fetch(u.toString(), { headers: await dmHeaders() });
  if (!r.ok) throw new Error(`DM customer failed ${r.status}`);
  return r.json();
}

  export async function getJobData(jobId) {
    return idbGet('jobData', jobId);
  }

  export async function opCodeCheck(jobId) {

    const jobData = await getJobData(jobId);
    console.log(jobData);
    if (!jobData) {
      console.error(`No job data found for ${jobId}`);
      return null;
    }
    const opCode = jobData.OpCode;
    const opCodeDataBase = ref(database, 'opcodes'); // Reference to /opcode
  
    try {
      // Fetch the data for the given opCode
      const snapshot = await firebaseGet(child(opCodeDataBase, opCode));
      if (snapshot.exists()) {
        return snapshot.val(); // Return the data if it exists
      } else {
        return null; // Return null if the opcode doesn't exist
      }
    } catch (error) {
      console.error("Error fetching opcode data:", error);
      return null; // Handle errors gracefully
    }
  }

// "WO 700007" for DockMaster numbers, but internal numbers are stored as "WO-2224" already:
// don't prefix them twice ("WO WO-2224"). `sep` is ' ' or ' #' to match the surrounding text.
export function woLabel(wo, sep = ' ') {
  const v = String(wo ?? '').trim();
  return /^WO[-\s#]/i.test(v) ? v : `WO${sep}${v}`;
}

// Installs and removals share Op_Type 'Shrinkwrap' (shrinkwrapCore.js has the rule).
export { isShrinkwrapInstall } from './shrinkwrapCore';

export function escapeHTML(str) {
  return String(str ?? '').replace(/[&<>"']/g, char => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[char]));
}

export function toProperCase(str) {
    return String(str ?? '').toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}

export async function getCustomerDMID(customerId) {
  const customerData = await idbGet('customerData', customerId);
  return customerData ? customerData.DM_Customer : null;
}

export async function getWorkOrderInfoDM(workOrder, billDate, invoiceNumber) {
  try {
    const result = await fetchDMWorkOrder(workOrder, billDate, invoiceNumber);
    if (!result || !result.Operations || result.Operations.length === 0) return [];

    const data = [];
    result.Operations.forEach(op => {
      data.push({
        Charge: `$${Number(op.TotalCharges).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        Description: op.LongDesc,
        Work_Order: workOrder,
        Bill_Date: billDate,
        Invoice: invoiceNumber
      });
    });
    return data;
  } catch (error) {
    console.log(error);
    return [];
  }
}


export async function getCustomerBalance(customer) {
  if (!usesDockmaster()) return null;
  try {
    const result = await fetchDockmasterCustomer(customer);
    if (!result?.Invoices) return null;
    return result;
  } catch (error) {
    console.error('Error fetching customer balance:', error);
    return null;
  }
}


// Fuel prices: DockMaster marinas read them from DockMaster; every other marina's office sets
// them in Fuel Dashboard › Fuel prices (/fuelPrices/current, admin/office write, staff read).
// Resolves with the signed-in user, or null after timeoutMs (so a page's start-up never hangs).
const signedIn = (timeoutMs = 0) => new Promise(resolve => {
  if (auth.currentUser) { resolve(auth.currentUser); return; }
  let timer = null;
  const stop = onAuthStateChanged(auth, u => { if (u) { clearTimeout(timer); stop(); resolve(u); } });
  if (timeoutMs) timer = setTimeout(() => { stop(); resolve(null); }, timeoutMs);
});

export async function getFuelPrices() {
  if (!usesDockmaster()) {
    if (!(await signedIn(15000))) return {};
    const snap = await firebaseGet(ref(database, 'fuelPrices/current'));
    const p = snap.val();
    return p ? { gasPrice: p.gasPrice, gasCost: p.gasCost, dieselPrice: p.dieselPrice, dieselCost: p.dieselCost, updatedAt: p.updatedAt, updatedBy: p.updatedBy } : {};
  }
  const u = new URL(`${FUNCTIONS_BASE}/dockmasterRetrieveFuel`);
  const r = await fetch(u.toString(), { headers: await dmHeaders() });
  if (!r.ok) throw new Error(`DM fuel failed ${r.status}`);
  const fuelData = await r.json();

  const diesel = fuelData.find((f) => f.Id.toLowerCase() === 'diesel');
  const gas = fuelData.find((f) => f.Id.toLowerCase() === 'gas');

  return {
    dieselPrice: diesel?.Price1,
    dieselCost: diesel?.Cost,
    gasPrice: gas?.Price1,
    gasCost: gas?.Cost
  };
}

/** Set today's fuel prices (marinas not on DockMaster; admin/office). Each change is kept in
 *  /fuelPrices/history. Readings already logged keep the price they were logged at. */
export async function saveFuelPrices({ gasPrice, gasCost, dieselPrice, dieselCost }) {
  const user = await signedIn(10000);
  if (!user) throw new Error('Not signed in');
  const entry = {
    gasPrice, gasCost, dieselPrice, dieselCost,
    updatedAt: Date.now(),
    updatedBy: String(user.displayName || user.email || user.uid).slice(0, 100),
  };
  const key = dbPush(ref(database, 'fuelPrices/history')).key;
  await dbUpdate(ref(database, 'fuelPrices'), { current: entry, [`history/${key}`]: entry });
  return entry;
}


// ─── General Ledger (Dockmaster GL) ──────────────────────────────────────
// Account-code classification is inferred from a real sample of this
// marina's chart of accounts (89 accounts, 860 transactions), not from
// Dockmaster documentation — there is no chart-of-accounts or account-type
// endpoint in their API. Every account seen is 7 digits, "100" + a type
// digit + a 3-digit detail code (e.g. 1004310 = Sales-Gas). The type digit
// is read from the 4th-from-last character so this still works if a
// different-length prefix shows up on an account this marina hasn't hit yet.
//   1 = Asset, 2 = Liability, 3 = Equity (unconfirmed, none seen in sample),
//   4 = Revenue ("Sales-*"), 5 = COGS ("COS-*"), 6/7/8 = Expense, 9 = Other Income
export function classifyGLAccount(code) {
  const c = String(code || '');
  const seg = c.length >= 4 ? c.charAt(c.length - 4) : '';
  switch (seg) {
    case '1': return 'asset';
    case '2': return 'liability';
    case '3': return 'equity';
    case '4': return 'revenue';
    case '5': return 'cogs';
    case '6': case '7': case '8': return 'expense';
    case '9': return 'otherIncome';
    default: return 'unknown';
  }
}

export async function getGLJournalTransactions(beginPostDate, endPostDate) {
  // Sidelined 2026-07-22: dockmasterRetrievePostedJournalTransactions is an open
  // HTTP endpoint (CORS-only, no ID-token/admin-claim check) — do not re-enable
  // this call until that Cloud Function verifies the caller server-side.
  throw new Error('Dockmaster GL fetch temporarily disabled pending admin-only auth fix');
  // const u = new URL(`${FUNCTIONS_BASE}/dockmasterRetrievePostedJournalTransactions`);
  // u.searchParams.set('beginPostDate', beginPostDate);
  // if (endPostDate) u.searchParams.set('endPostDate', endPostDate);
  // const r = await fetch(u.toString());
  // if (!r.ok) throw new Error(`DM GL failed ${r.status}`);
  // return r.json();
}

// Rolls a raw journal-transaction list up into Revenue/COGS/Expense/Other
// Income totals (credit - debit, the normal sign for revenue-side accounts —
// expense/COGS totals come out negative, which is what netIncome expects).
// Deferred-revenue movement is matched by description substring ("Deferred
// Revenue" appears in every such account seen) rather than hardcoded codes,
// so a new deferred-revenue sub-account picked up later still counts.
export function summarizeGLTransactions(transactions) {
  const totals = { revenue: 0, cogs: 0, expense: 0, otherIncome: 0, deferredRevenueMovement: 0 };
  (transactions || []).forEach(tx => {
    (tx.TransactionLines || []).forEach(line => {
      const net = (parseFloat(line.CreditAmount) || 0) - (parseFloat(line.DebitAmount) || 0);
      const type = classifyGLAccount(line.GLAccount);
      if (type === 'revenue') totals.revenue += net;
      else if (type === 'cogs') totals.cogs += net;
      else if (type === 'expense') totals.expense += net;
      else if (type === 'otherIncome') totals.otherIncome += net;
      if ((line.GLAccountDesc || '').toLowerCase().includes('deferred revenue')) {
        totals.deferredRevenueMovement += net;
      }
    });
  });
  totals.grossProfit = totals.revenue + totals.cogs;
  totals.netIncome = totals.revenue + totals.cogs + totals.expense + totals.otherIncome;
  return totals;
}

// Month-to-date financial performance off Dockmaster's real GL — this is a
// live call (like fetchTide/fetchWeather elsewhere on the Dashboard), not
// cached, so it reflects whatever has actually been posted as of "now".
// Returns null on failure so the caller can show an "unavailable" state
// instead of a silently wrong $0.
export async function getFinancialPerformance() {
  if (!usesDockmaster()) return null;
  try {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const monthStart = ymd(new Date(now.getFullYear(), now.getMonth(), 1));
    const today = ymd(now);

    const transactions = await getGLJournalTransactions(monthStart, today);
    const totals = summarizeGLTransactions(transactions);

    return {
      ...totals,
      monthLabel: now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      asOf: today
    };
  } catch (error) {
    console.error('[utilities] getFinancialPerformance failed', error);
    return null;
  }
}



export async function checkAndUpdateDMToken() {
  if (!usesDockmaster()) return null;
  const apiRef = child(ref(database), 'API');
  const snapshot = await firebaseGet(apiRef);
  if (!snapshot.exists()) return null;
  const dmConfig = snapshot.val();

  const twoDaysInMillis = 2 * 24 * 60 * 60 * 1000;
  const lastUpdated = dmConfig.lastUpdated || 0;

  if (Date.now() - lastUpdated > twoDaysInMillis) {
    const functions = getFunctions();
    const updateToken = httpsCallable(functions, 'getDMTokenAndUpdateManual');
    
    try {
      await updateToken();
    } catch (error) {
      console.error('Error updating token:', error);
      throw error;
    }

    const updatedSnapshot = await firebaseGet(apiRef);
    return updatedSnapshot.val().DockmasterDMToken;
  }

  return dmConfig.DockmasterDMToken;
}

const ROLE_CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

export async function getUserRole(uid) {
  if (!uid) return null;

  const cachedRole = localStorage.getItem('currentUserRole');
  const cachedAt = parseInt(localStorage.getItem('currentUserRoleCachedAt') ?? '0', 10);
  const cacheValid = cachedRole && (Date.now() - cachedAt) < ROLE_CACHE_TTL_MS;

  if (!navigator.onLine) {
    return cacheValid ? cachedRole : (localStorage.getItem('currentUserSecurity') ?? null);
  }

  try {
    const userRoleRef = ref(database, `users/${uid}/role`);
    const read = firebaseGet(userRoleRef);
    // A slow connection shouldn't hold the page on its splash when this device already
    // knows the role: after 8 s use the cached one (the read still refreshes the cache).
    if (cacheValid) {
      let timer;
      const slow = new Promise(resolve => { timer = setTimeout(() => resolve(null), 8000); });
      const snap = await Promise.race([read, slow]).finally(() => clearTimeout(timer));
      if (!snap) {
        read.then(s => { if (s.exists()) { localStorage.setItem('currentUserRole', s.val()); localStorage.setItem('currentUserRoleCachedAt', String(Date.now())); } }).catch(() => {});
        return cachedRole;
      }
    }
    const snapshot = await read;
    const role = snapshot.exists() ? snapshot.val() : null;

    if (role) {
      localStorage.setItem('currentUserRole', role);
      localStorage.setItem('currentUserRoleCachedAt', String(Date.now()));
      if (isAdminRole(String(role))) localStorage.setItem('currentUserSecurity', 'admin');
    }

    return role;
  } catch (error) {
    return cacheValid ? cachedRole : (localStorage.getItem('currentUserSecurity') ?? null);
  }
}


export async function fetchOperationsForWorkOrder(workOrderNumber) {
  return idbCursorAll('jobData', undefined, job =>
    job.Work_Order === workOrderNumber &&
    job.Status?.toString() !== 'Declined'
  );
}

export async function loadBoatsFromDatabase() {
  return idbCursorAll('mapDataBoats');
}

// Storage Contract Builder (Phase 3) + Settings > Storage rates (Phase 2)
// share this same catalog — defined once here so settings.js's mapping
// table and app.js's contract builder can't drift apart. Transcribed from
// the Winter Storage & Spring Commissioning Agreement; `rate` is the
// display string shown in both UIs, `amount`/`unit` are what the builder
// actually multiplies against the boat's Length to compute a line price.
// unit 'tm' (time & materials) has no fixed amount — excluded from the
// contract total, shown as "T&M" per the printed agreement's own convention.
// opCode is left blank (unmapped) except where a real op code is already
// known from elsewhere in the app — guessing at fake Dockmaster codes would
// be actively wrong, so everything else starts as an honest "Choose…" in
// Settings and an unmapped row at contract review (Phase 4).
// The 4 "Shrink wrap & frame" rows below are alternatives, not independent
// add-ons — exactly one applies to any given boat, picked by length via
// minLength/maxLength (see contractServiceApplies() in app.js). Every other
// catalog entry (including the flat-rate "Shrink wrap door") is
// length-agnostic and has no min/max, so it always renders.
// Tech skill categories (rated per tech in Settings > Team) — the Scheduler matches
// jobs to techs by these.
export const TECH_SKILLS = [
  'Bottom Painting', 'Cleaning', 'Electrical', 'Fiberglass', 'Hauling',
  'Launching', 'Mechanical', 'Moorings', 'Piloting', 'Rigging', 'Wood Work',
];

// The work-order form's Op Type list (index.html #opType) — a different taxonomy.
export const WO_OP_TYPES = [
  'Anodes', 'Batteries', 'Bottom Paint', 'Canvas', 'Cleaning', 'Electrical', 'Electronics', 'Fiberglass', 'Hauling',
  'Mechanical', 'Moorings', 'Piloting', 'Rigging', 'Shrinkwrap', 'Wood Work', 'Yard Work',
];

// Default Op_Type -> tech skill for types not named like a skill. Editable per
// business at /settings/opTypeSkillMap (Settings > Op codes > Skill mapping); a
// saved value of '' there means "guess from the description".
export const DEFAULT_OP_TYPE_TO_SKILL = {
  'Bottom Paint': 'Bottom Painting',
  Batteries: 'Electrical',
  Electronics: 'Electrical',
  Anodes: 'Mechanical',
  Shrinkwrap: 'Hauling',
  'Yard Work': 'Hauling',
  // Op_Type values seen in /opcodes that aren't on the WO form's list.
  'Launch Service': 'Launching',
  Storage: 'Hauling',
  Yard: 'Hauling',
};

export function defaultSkillForOpType(opType) {
  if (TECH_SKILLS.includes(opType)) return opType;
  return DEFAULT_OP_TYPE_TO_SKILL[opType] || '';
}

// The storage contract catalog, defaults and pricing live in storageContractCore.js (shared with
// the portal and Cloud Functions); re-exported here for the office screens that import them from here.
export { STORAGE_SERVICE_CATALOG, STORAGE_DEFAULTS, currentSeasonLabel } from './storageContractCore';


// Inside the office shell every page is an iframe, which keeps its own keystrokes — so the
// shell's shortcuts are handed up to it from here, the one module nearly every page loads:
// "/" and Ctrl+K (search), N (new work order), ? (the list) and G then a letter (go to a page;
// shellKeys.js). Ignored while typing, and outside the shell.
if (typeof window !== 'undefined' && window.top !== window) {
  let gAt = 0;
  document.addEventListener('keydown', (e) => {
    if (e.altKey || e.defaultPrevented) return;
    const a = document.activeElement;
    if (a && (a.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName))) return;
    const ctrlK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
    if ((e.ctrlKey || e.metaKey) && !ctrlK) return;
    const key = e.key === '?' ? '?' : e.key.toLowerCase();
    const afterG = gAt && Date.now() - gAt < 1500;
    let msg = null;
    if (e.key === '/') msg = { type: 'SHELL_SEARCH' };
    else if (ctrlK) msg = { type: 'SHELL_KEY', key: 'k', ctrlK: true };
    else if (key === '?' || key === 'n' || key === 'g' || (afterG && e.key.length === 1)) msg = { type: 'SHELL_KEY', key };
    if (!ctrlK) gAt = key === 'g' && !afterG ? Date.now() : 0;
    if (!msg) return;
    try {
      if (window.top.location.origin !== window.location.origin) return;
      e.preventDefault();
      window.top.postMessage(msg, window.location.origin);
    } catch { /* not inside the shell */ }
  });
}
